<template>
  <main class="bills-page">
    <!-- Period tabs -->
    <el-tabs v-model="activePeriod" @tab-click="onPeriodChange">
      <el-tab-pane label="年度" name="annual" />
      <el-tab-pane label="月度" name="monthly" />
    </el-tabs>

    <!-- Summary cards -->
    <section class="summary-cards">
      <div class="summary-card">
        <div class="card-label">总 Token</div>
        <div class="card-value">{{ $formatNumber(summary.total_tokens) }}</div>
      </div>
      <div class="summary-card">
        <div class="card-label">费用</div>
        <div class="card-value cost-color">¥ {{ $formatNumber(summary.total_cost) }}</div>
      </div>
      <div class="summary-card" v-if="summary.admin">
        <div class="card-label">供应商成本</div>
        <div class="card-value muted-color">¥ {{ $formatNumber(summary.supplier_cost) }}</div>
      </div>
      <div class="summary-card" v-if="summary.admin">
        <div class="card-label">利润</div>
        <div class="card-value" :class="summary.profit >= 0 ? 'profit-positive' : 'profit-negative'">
          ¥ {{ $formatNumber(summary.profit) }}
        </div>
      </div>
      <div class="summary-card">
        <div class="card-label">调用次数</div>
        <div class="card-value">{{ $formatNumber(summary.total_calls) }}</div>
      </div>
      <div class="summary-card" v-if="summary.personal">
        <div class="card-label">本月消费</div>
        <div class="card-value cost-color">¥ {{ $formatNumber(summary.month_cost) }}</div>
      </div>
      <div class="summary-card" v-if="summary.personal">
        <div class="card-label">月上限</div>
        <div class="card-value" :class="summary.monthly_budget > 0 ? 'cost-color' : 'muted-color'">
          {{ summary.monthly_budget > 0 ? '¥ ' + $formatNumber(summary.monthly_budget) : '无上限' }}
        </div>
      </div>
    </section>

    <!-- Trend chart -->
    <section class="soft-card chart-panel" v-if="trendData.length">
      <div class="panel-head">
        <h3 class="panel-title">消费趋势</h3>
        <span class="panel-sub">{{ rangeLabel }}</span>
      </div>
      <div ref="trendChart" class="chart-container" />
    </section>

    <!-- Items table -->
    <section class="soft-card table-panel">
      <div class="toolbar">
        <span class="toolbar-title">{{ periodTitle }}</span>
        <el-date-picker
          v-if="activePeriod === 'annual'"
          v-model="pickerYear"
          type="year"
          placeholder="选择年份"
          size="small"
          value-format="yyyy"
          class="date-picker"
          @change="onDateChange"
        />
        <el-date-picker
          v-else
          v-model="pickerMonth"
          type="month"
          placeholder="选择月份"
          size="small"
          value-format="yyyy-MM"
          class="date-picker"
          @change="onDateChange"
        />
        <el-button size="small" icon="el-icon-download" @click="onExport">导出</el-button>
      </div>
      <div class="desktop-only">
        <el-table :data="items" size="small" style="width: 100%" v-loading="loading">
          <el-table-column type="index" label="序号" width="60" :index="pageIndex" />
          <el-table-column label="周期">
            <template slot-scope="scope">
              <el-button v-if="scope.row.year" type="text" size="small" @click="onDrillDown(scope.row)">
                {{ scope.row.label }}
              </el-button>
              <span v-else>{{ scope.row.label }}</span>
            </template>
          </el-table-column>
          <el-table-column label="总 Token" align="right" min-width="110">
            <template slot-scope="scope">{{ $formatNumber(scope.row.total_tokens) }}</template>
          </el-table-column>
          <el-table-column label="调用次数" align="right" min-width="90">
            <template slot-scope="scope">{{ scope.row.total_calls }}</template>
          </el-table-column>
          <el-table-column label="费用" align="right" min-width="130">
            <template slot-scope="scope">
              <el-button type="text" size="small" class="cost-link" @click="onCostClick(scope.row)">
                ¥ {{ $formatNumber(scope.row.total_cost) }}
              </el-button>
            </template>
          </el-table-column>
          <el-table-column v-if="summary.admin" label="供应商成本" align="right" min-width="110">
            <template slot-scope="scope">¥ {{ $formatNumber(scope.row.supplier_cost) }}</template>
          </el-table-column>
          <el-table-column v-if="summary.admin" label="利润" align="right" min-width="110">
            <template slot-scope="scope">
              <span :class="scope.row.profit >= 0 ? 'profit-positive' : 'profit-negative'">
                ¥ {{ $formatNumber(scope.row.profit) }}
              </span>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div class="mobile-only list-cards">
        <div class="list-card" v-for="(row, idx) in items" :key="idx">
          <div class="card-header">
            <div class="card-title">{{ row.label }}</div>
          </div>
          <div class="stats-row">
            <div class="stat-item">
              <div class="stat-value">{{ $formatNumber(row.total_tokens) }}</div>
              <div class="stat-label">总 Token</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">{{ row.total_calls }}</div>
              <div class="stat-label">调用次数</div>
            </div>
          </div>
          <div class="meta-row">
            <span class="cost-link" style="font-size:13px;font-weight:700;" @click="onCostClick(row)">¥ {{ $formatNumber(row.total_cost) }}</span>
            <span v-if="summary.admin && row.supplier_cost != null">成本 ¥ {{ $formatNumber(row.supplier_cost) }}</span>
          </div>
        </div>
      </div>
      <el-pagination
        class="pagination-bar"
        layout="total, prev, pager, next"
        small
        :total="total"
        :page-size="pageSize"
        :current-page.sync="currentPage"
        @current-change="onPageChange"
      />
    </section>

    <!-- 用户用量汇总弹窗 -->
    <el-dialog :title="accountDialogTitle" :visible.sync="accountDialogVisible" width="860px" top="6vh">
      <el-table :data="accountRows" size="small" style="width: 100%" v-loading="accountLoading" max-height="560"
                :default-sort="accountDefaultSort" @sort-change="onAccountSortChange">
        <el-table-column type="index" label="序号" width="60" :index="accountIndex" />
        <el-table-column prop="username" label="账号" min-width="180" show-overflow-tooltip />
        <el-table-column label="姓名" min-width="140" show-overflow-tooltip>
          <template slot-scope="scope">{{ scope.row.name || '-' }}</template>
        </el-table-column>
        <el-table-column prop="total_calls" label="调用次数" align="right" min-width="110" sortable="custom">
          <template slot-scope="scope">{{ $formatNumber(scope.row.total_calls) }}</template>
        </el-table-column>
        <el-table-column prop="total_tokens" label="总 Token" align="right" min-width="130" sortable="custom">
          <template slot-scope="scope">{{ $formatNumber(scope.row.total_tokens) }}</template>
        </el-table-column>
        <el-table-column prop="total_cost" label="费用" align="right" min-width="130" sortable="custom">
          <template slot-scope="scope">
            <span class="cost-amount">¥ {{ $formatNumber(scope.row.total_cost) }}</span>
          </template>
        </el-table-column>
      </el-table>
      <div v-if="Number(accountTotal) > 0" class="account-total">
        <span>合计：{{ $formatNumber(accountSummary.total_calls) }} 次 · {{ $formatNumber(accountSummary.total_tokens) }} tokens · </span>
        <span class="cost-amount">¥ {{ $formatNumber(accountSummary.total_cost) }}</span>
      </div>
      <div v-else-if="!accountLoading" class="account-empty">该周期暂无用户用量</div>
      <el-pagination
        v-if="Number(accountTotal) > 0"
        class="account-pagination"
        layout="total, prev, pager, next"
        small
        :total="Number(accountTotal)"
        :page-size="accountPageSize"
        :current-page.sync="accountPage"
        @current-change="onAccountPageChange"
      />
    </el-dialog>
  </main>
</template>

<script>
import * as echarts from 'echarts'
import * as XLSX from 'xlsx'

export default {
  name: 'BillsPage',
  data() {
    const now = new Date()
    return {
      activePeriod: 'annual',
      pickerYear: String(now.getFullYear()),
      pickerMonth: now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0'),
      summary: {
        totalTokens: 0,
        totalCost: 0,
        supplierCost: 0,
        profit: 0,
        totalCalls: 0,
        admin: false
      },
      trendData: [],
      items: [],
      total: 0,
      pageSize: 10,
      currentPage: 1,
      loading: false,
      chart: null,
      accountDialogVisible: false,
      accountDialogTitle: '',
      accountDialogDate: '',
      accountLoading: false,
      accountRows: [],
      accountTotal: 0,
      accountPage: 1,
      accountPageSize: 20,
      accountSortField: 'total_cost',
      accountSortOrder: 'desc',
      accountSummary: {}
    }
  },
  computed: {
    pageIndex() {
      return (this.currentPage - 1) * this.pageSize + 1
    },
    accountIndex() {
      return (this.accountPage - 1) * this.accountPageSize + 1
    },
    accountDefaultSort() {
      return { prop: 'total_cost', order: 'descending' }
    },
    periodTitle() {
      const map = { monthly: '月度账单', annual: '年度账单' }
      return map[this.activePeriod] || '账单'
    },
    rangeLabel() {
      return this.activePeriod === 'annual' ? this.pickerYear + '年' : this.pickerMonth
    }
  },
  mounted() {
    this.fetchAll()
  },
  beforeDestroy() {
    if (this.chart) { this.chart.dispose(); this.chart = null }
  },
  methods: {
    async fetchAll() {
      this.loading = true
      try {
        const params = this.buildParams()
        const [summary, trend] = await Promise.all([
          this.$http.get('/admin/billing/summary', { params }),
          this.$http.get('/admin/billing/trend', { params })
        ])
        this.summary = summary
        this.trendData = trend || []
        await this.fetchItems()
        this.$nextTick(() => this.renderChart())
      } catch {
        this.$message.error('加载账单数据失败')
      } finally {
        this.loading = false
      }
    },
    async fetchItems() {
      try {
        const params = { ...this.buildParams(), page: this.currentPage, pageSize: this.pageSize }
        const res = await this.$http.get('/admin/billing/items', { params })
        this.items = res.records || []
        this.total = Number(res.total) || 0
      } catch {
        this.$message.error('加载账单明细失败')
      }
    },
    buildParams() {
      const params = { period: this.activePeriod }
      if (this.activePeriod === 'annual') {
        params.year = parseInt(this.pickerYear) || new Date().getFullYear()
      } else {
        const parts = (this.pickerMonth || '').split('-')
        params.year = parseInt(parts[0]) || new Date().getFullYear()
        params.month = parseInt(parts[1]) || (new Date().getMonth() + 1)
      }
      return params
    },
    onCostClick(row) {
      const date = this.rowPeriodDate(row)
      if (!date) return
      this.openAccountSummary(date)
    },
    rowPeriodDate(row) {
      const pad = n => String(n).padStart(2, '0')
      if (!row || !row.year || !row.month) return ''
      if (row.day) return `${row.year}-${pad(row.month)}-${pad(row.day)}`
      return `${row.year}-${pad(row.month)}`
    },
    async openAccountSummary(date) {
      this.accountDialogDate = date
      this.accountDialogTitle = `${date} 用户用量汇总`
      this.accountDialogVisible = true
      this.accountPage = 1
      this.accountSortField = 'total_cost'
      this.accountSortOrder = 'desc'
      await this.fetchAccountSummary()
    },
    async fetchAccountSummary() {
      if (!this.accountDialogDate) return
      this.accountLoading = true
      try {
        const res = await this.$http.get('/admin/billing/account-summary', {
          params: {
            date: this.accountDialogDate,
            page: this.accountPage,
            pageSize: this.accountPageSize,
            sortField: this.accountSortField,
            sortOrder: this.accountSortOrder
          }
        })
        this.accountRows = res.records || []
        this.accountTotal = Number(res.total) || 0
        this.accountSummary = res.summary || {}
      } catch {
        this.$message.error('加载用户用量失败')
      } finally {
        this.accountLoading = false
      }
    },
    onAccountSortChange({ prop, order }) {
      this.accountSortField = prop || 'total_cost'
      this.accountSortOrder = order === 'ascending' ? 'asc' : 'desc'
      this.accountPage = 1
      this.fetchAccountSummary()
    },
    onAccountPageChange(page) {
      this.accountPage = page
      this.fetchAccountSummary()
    },
    onPeriodChange() {
      this.currentPage = 1
      this.fetchAll()
    },
    onDateChange() {
      this.currentPage = 1
      this.fetchAll()
    },
    onPageChange(page) {
      this.currentPage = page
      this.fetchItems()
    },
    onDrillDown(row) {
      if (this.activePeriod === 'annual' && row.year && row.month) {
        this.activePeriod = 'monthly'
        this.pickerMonth = row.year + '-' + String(row.month).padStart(2, '0')
        this.fetchAll()
      }
    },
    onExport() {
      const params = this.buildParams()
      this.$http.get('/admin/billing/export', { params }).then(records => {
        if (!records || !records.length) {
          this.$message.info('没有数据可导出')
          return
        }
        const isAdmin = this.summary.admin
        const data = records.map(r => {
          const row = { '周期': r.label, '总Token': r.total_tokens, '调用次数': r.total_calls, '费用': r.total_cost }
          if (isAdmin) {
            row['供应商成本'] = r.supplier_cost
            row['利润'] = r.profit
          }
          return row
        })
        const ws = XLSX.utils.json_to_sheet(data)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, this.periodTitle)
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' })
        const blob = new Blob([wbout], { type: 'application/octet-stream' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        const ts = new Date()
        const pad = n => String(n).padStart(2, '0')
        a.download = `${this.periodTitle}_${ts.getFullYear()}${pad(ts.getMonth()+1)}${pad(ts.getDate())}${pad(ts.getHours())}${pad(ts.getMinutes())}${pad(ts.getSeconds())}.xlsx`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        this.$message.success('导出成功')
      }).catch(() => {
        this.$message.error('导出失败')
      })
    },
    renderChart() {
      const el = this.$refs.trendChart
      if (!el || !this.trendData.length) return
      if (this.chart) this.chart.dispose()
      const chart = echarts.init(el)
      this.chart = chart

      const dates = this.trendData.map(d => this.formatLabel(d.date))
      const costs = this.trendData.map(d => Number(d.cost) || 0)

      chart.setOption({
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(20,25,40,0.9)',
          borderColor: 'rgba(133,164,255,0.2)',
          textStyle: { color: '#c8d0e0', fontSize: 12 }
        },
        grid: { left: 60, right: 20, top: 20, bottom: 30 },
        xAxis: {
          type: 'category',
          data: dates,
          axisLine: { lineStyle: { color: 'rgba(133,164,255,0.15)' } },
          axisLabel: { color: '#9aaac1', fontSize: 11, rotate: dates.length > 24 ? 45 : 0 }
        },
        yAxis: {
          type: 'value',
          splitLine: { lineStyle: { color: 'rgba(133,164,255,0.08)' } },
          axisLabel: { color: '#9aaac1', fontSize: 11, formatter: v => '¥' + v }
        },
        series: [{
          type: 'line',
          data: costs,
          smooth: true,
          symbol: 'none',
          lineStyle: { width: 2, color: '#34c98f' },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: 'rgba(52,201,143,0.28)' },
              { offset: 1, color: 'rgba(52,201,143,0)' }
            ])
          }
        }]
      })
    },
    formatLabel(date) {
      if (!date) return date
      if (this.activePeriod === 'annual') {
        const parts = date.split('-')
        return parseInt(parts[1]) + '月'
      }
      const parts = date.split('-')
      return parseInt(parts[2]) + '日'
    }
  }
}
</script>

<style scoped>
.bills-page {
  padding: 0 24px 76px;
}

.cost-link {
  color: #34c98f;
  font-weight: 700;
  cursor: pointer;
  padding: 0;
}
.cost-link:hover {
  color: #55f0b3;
  text-decoration: underline;
}

.account-total {
  margin-top: 12px;
  text-align: right;
  font-size: 13px;
  color: var(--text);
  font-weight: 700;
}

.account-pagination {
  margin-top: 10px;
  text-align: right;
}

.account-empty {
  padding: 24px 0;
  text-align: center;
  color: var(--muted-2);
  font-size: 13px;
}

.bills-page ::v-deep .el-tabs__header {
  border-bottom: 1px solid rgba(133, 164, 255, 0.15);
  margin-bottom: 20px;
}

.bills-page ::v-deep .el-tabs__item {
  color: rgba(255, 255, 255, 0.35);
  font-size: 14px;
  font-weight: 800;
  height: 44px;
  line-height: 44px;
  letter-spacing: 0.02em;
}

.bills-page ::v-deep .el-tabs__item:hover {
  color: var(--text);
}

.bills-page ::v-deep .el-tabs__item.is-active {
  color: var(--text);
}

.bills-page ::v-deep .el-tabs__active-bar {
  background: #55f0b3;
  height: 2.5px;
  border-radius: 2px;
}

.bills-page ::v-deep .el-tabs__nav-wrap::after {
  background: transparent;
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 14px;
  margin-bottom: 20px;
}

.summary-card {
  padding: 16px;
  border: 1px solid var(--line);
  border-radius: var(--radius);
  background: linear-gradient(180deg, rgba(27, 39, 58, 0.9), rgba(20, 30, 46, 0.88));
  box-shadow: var(--shadow);
}

.card-label {
  font-size: 12px;
  color: var(--muted);
  margin-bottom: 8px;
  font-weight: 760;
}

.card-value {
  font-size: 22px;
  font-weight: 900;
  color: var(--text);
  line-height: 1.1;
}

.cost-color { color: #34c98f; }
.muted-color { color: var(--muted-2); }
.profit-positive { color: #34c98f; }
.profit-negative { color: #ff6b7a; }

.chart-panel {
  padding: 18px;
  margin-bottom: 16px;
}

.chart-container {
  width: 100%;
  height: 260px;
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.panel-title {
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  color: var(--text);
}

.panel-sub {
  color: var(--muted);
  font-size: 12px;
}

.table-panel {
  padding: 16px;
  border-radius: var(--radius);
  box-shadow: var(--shadow);
}

.table-panel ::v-deep .el-table,
.table-panel ::v-deep .el-table__header-wrapper,
.table-panel ::v-deep .el-table__body-wrapper {
  border-radius: var(--radius);
}

.table-panel ::v-deep th {
  background: rgba(255, 255, 255, 0.045);
  color: #9eb1ca;
  font-weight: 850;
}

.table-panel ::v-deep td,
.table-panel ::v-deep th {
  border-color: var(--line-soft);
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
}

.toolbar-title {
  font-size: 16px;
  font-weight: 800;
  color: var(--text);
  flex: 1;
}

.date-picker {
  width: 160px;
}

.pagination-bar {
  margin-top: 20px;
  text-align: right;
}

/* mobile cards */
.list-cards { display: grid; gap: 14px; }
.mobile-only { display: none; }

@media (max-width: 760px) {
  .desktop-only { display: none; }
  .mobile-only { display: block; }
  .bills-page {
    padding: 0 12px 28px;
  }
  .table-panel {
    padding: 10px;
  }
  .toolbar {
    flex-wrap: wrap;
  }
  .date-picker {
    flex: 1;
    min-width: 120px;
  }
  .pagination-bar {
    text-align: center;
  }
  .summary-cards {
    grid-template-columns: repeat(2, 1fr);
  }
}

.list-card {
  padding: 14px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(27,39,58,.92), rgba(20,30,46,.88));
  border: 1px solid rgba(133,164,255,.12);
  box-shadow: 0 8px 28px rgba(0,0,0,.22);
}

.card-header {
  margin-bottom: 10px;
}

.card-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--text);
}

.stats-row {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
}

.stat-item {
  flex: 1;
  background: rgba(255,255,255,.04);
  border-radius: 8px;
  padding: 8px 10px;
  text-align: center;
}

.stat-value {
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
}

.stat-label {
  font-size: 10px;
  color: rgba(255,255,255,.35);
  margin-top: 2px;
}

.meta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 12px;
  padding-top: 8px;
  border-top: 1px solid rgba(133,164,255,0.06);
  font-size: 12px;
  color: var(--muted-2);
}
</style>
